import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Filter, Download, AlertTriangle, Eye, X, CheckCircle, CheckCircle2,
  Clock, Package, PackageCheck, ShoppingCart, CreditCard, Truck, ChevronRight,
  RefreshCw, ClipboardCheck, UserPlus, FileText, RotateCcw, Scan, User, Shield,
  Undo2, Pencil, Copy, SlidersHorizontal, Calendar, Trash2, MoreHorizontal, Ban
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../../ui/dropdown-menu';
import { API, formatPrice, formatDate, STATUS_BADGES, PAYMENT_STATUS_BADGES, formatActivityType, getActivityTypeBadgeClass } from '../utils';
import SEPARequestDetailModal from '../SEPARequestDetailModal';
import { Building2 } from 'lucide-react';

const PackName = ({ name }) => <span>{name}</span>;

const getStatusBadge = (status) => {
  const badge = STATUS_BADGES[status] || STATUS_BADGES.pending;
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>{badge.text}</span>;
};

const OrdersView = ({ reservations, handleApproveBooking, handleRejectBooking }) => {
  const navigate = useNavigate();
const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [clientDossier, setClientDossier] = useState(null);
    const [loadingDossier, setLoadingDossier] = useState(false);
    const [returnsToProcess, setReturnsToProcess] = useState([]);
    const [refundingOrder, setRefundingOrder] = useState(null);  // reservation object being refunded
    const [refundPassword, setRefundPassword] = useState('');
    const [refundReason, setRefundReason] = useState('Rétractation client');
    const [refundLoading, setRefundLoading] = useState(false);
    // Suppression manuelle d'une commande (mot de passe dédié)
    const [deletingOrder, setDeletingOrder] = useState(null);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteMode, setDeleteMode] = useState('soft'); // 'soft' (corbeille) | 'permanent'
    // Corbeille (commandes archivées)
    const [activeTab, setActiveTab] = useState('en_cours');
    const [trashItems, setTrashItems] = useState([]);
    const [trashLoading, setTrashLoading] = useState(false);
    const [editingPack, setEditingPack] = useState(null);  // { id, pack_name, pack_surface, pack_id }
    const [editPackLoading, setEditPackLoading] = useState(false);
    const [catalogPacks, setCatalogPacks] = useState([]);
    // SEPA requests pending admin approval (shown as a banner + opens detail modal)
    const [sepaPending, setSepaPending] = useState([]);
    const [sepaDetailId, setSepaDetailId] = useState(null);
    const [sepaLoading, setSepaLoading] = useState(true);

    // Traiter le retour (transporteur + n° de suivi du RETOUR, distinct de l'envoi)
    const [returnShippingOrder, setReturnShippingOrder] = useState(null);
    const [returnCarrier, setReturnCarrier] = useState('Dachser');
    const [returnCarrierOther, setReturnCarrierOther] = useState('');
    const [returnTracking, setReturnTracking] = useState('');
    const [returnSendEmail, setReturnSendEmail] = useState(false);
    const [returnLabelUrl, setReturnLabelUrl] = useState('');
    const [returnShippingLoading, setReturnShippingLoading] = useState(false);

    // Manual status / payment editor
    const [statusEditOrder, setStatusEditOrder] = useState(null);
    const [editStatus, setEditStatus] = useState('');
    const [editPayment, setEditPayment] = useState('');
    const [statusSaving, setStatusSaving] = useState(false);

    // Résiliation initiée par l'admin (à la place du client)
    const [terminatingOrder, setTerminatingOrder] = useState(null);
    const [terminateReason, setTerminateReason] = useState('');
    const [terminatePassword, setTerminatePassword] = useState('');
    const [terminateLoading, setTerminateLoading] = useState(false);

    const submitTerminate = async () => {
      if (!terminatingOrder) return;
      if (!terminatePassword) { toast.error('Mot de passe requis'); return; }
      setTerminateLoading(true);
      try {
        await axios.post(
          `${API}/admin/orders/${terminatingOrder.id}/terminate`,
          { reason: (terminateReason || '').trim() || "Résiliation initiée par l'administrateur (client n'a pas résilié)", password: terminatePassword },
          { withCredentials: true }
        );
        toast.success('Location résiliée — la commande est passée dans l\'onglet « Résiliation », abonnement stoppé');
        setTerminatingOrder(null);
        setTerminateReason('');
        setTerminatePassword('');
        try {
          const response = await axios.get(`${API}/admin/returns-to-process`, { withCredentials: true });
          setReturnsToProcess(response.data || []);
        } catch { /* ignore refresh error */ }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la résiliation');
      } finally {
        setTerminateLoading(false);
      }
    };

    const openStatusEditor = (order) => {
      setStatusEditOrder(order);
      setEditStatus(order.status || 'pending_payment');
      setEditPayment(order.payment_status || 'pending');
    };

    const saveStatus = async () => {
      if (!statusEditOrder) return;
      setStatusSaving(true);
      try {
        await axios.put(
          `${API}/admin/orders/${statusEditOrder.id}/status`,
          { status: editStatus, payment_status: editPayment },
          { withCredentials: true }
        );
        toast.success('Statut mis à jour');
        setStatusEditOrder(null);
        window.location.reload();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
      } finally {
        setStatusSaving(false);
      }
    };

    const fetchPendingSEPA = async () => {
      try {
        const r = await axios.get(`${API}/admin/sepa-requests?status=pending_approval`, { withCredentials: true });
        setSepaPending(r.data || []);
      } catch (e) {
        // silent — keeps Orders page working even if endpoint not deployed
      } finally {
        setSepaLoading(false);
      }
    };

    useEffect(() => {
      fetchPendingSEPA();
    }, []);
    
    const pendingValidation = reservations.filter(r => r.status === 'pending_validation');
    const confirmedOrders = reservations.filter(r => r.status === 'confirmed');

    // Catégorisation des commandes par étape du cycle de location
    const SHIPPED_STATUSES = ['shipped', 'delivered', 'return_pending'];
    const ARCHIVED_STATUSES = ['returned', 'terminated', 'cancelled', 'rejected', 'fully_refunded'];
    // Une commande avec une demande de retour ACTIVE (résiliation en cours) = matériel qui revient
    const getActiveReturn = (r) => returnsToProcess.find(ret => ret.reservation_id === r.id);
    const hasActiveReturn = (r) => returnsToProcess.some(ret => ret.reservation_id === r.id);
    // Fusionne les réservations chargées avec celles portées par une résiliation mais
    // absentes de la liste (commande ancienne / au-delà de la limite de 1000). Ainsi
    // l'onglet Résiliation / Remise en stock affiche TOUTES les demandes en cours.
    const mergedReservations = React.useMemo(() => {
      const known = new Set(reservations.map(r => r.id));
      const extra = returnsToProcess
        .filter(ret => ret.reservation && !known.has(ret.reservation_id))
        .map(ret => ret.reservation);
      return extra.length ? [...reservations, ...extra] : reservations;
    }, [reservations, returnsToProcess]);
    // Statuts de retour où le transporteur/suivi est posé → matériel en route/arrivé = à réceptionner
    const RESTOCK_RETURN_STATUSES = ['label_sent', 'in_transit'];
    const categorizeOrder = (r) => {
      const rr = getActiveReturn(r);
      if (rr) return RESTOCK_RETURN_STATUSES.includes(rr.status) ? 'remise_en_stock' : 'retours';
      if (SHIPPED_STATUSES.includes(r.status)) return 'expedie';
      if (ARCHIVED_STATUSES.includes(r.status)) return 'archive';
      return 'en_cours';
    };
    const showTrash = activeTab === 'corbeille';
    const filteredReservations = mergedReservations.filter(r => categorizeOrder(r) === activeTab);
    const tabCounts = {
      en_cours: mergedReservations.filter(r => categorizeOrder(r) === 'en_cours').length,
      expedie: mergedReservations.filter(r => categorizeOrder(r) === 'expedie').length,
      retours: mergedReservations.filter(r => categorizeOrder(r) === 'retours').length,
      remise_en_stock: mergedReservations.filter(r => categorizeOrder(r) === 'remise_en_stock').length,
      archive: mergedReservations.filter(r => categorizeOrder(r) === 'archive').length,
    };
    
    const handleMarkDelivered = async (reservationId) => {
      if (!window.confirm('Confirmer la livraison ? Le mois de location démarrera à partir d\'aujourd\'hui.')) return;
      try {
        await axios.put(`${API}/admin/orders/${reservationId}/delivered`, {}, { withCredentials: true });
        toast.success('Commande marquée comme livrée. Le mois de location démarre maintenant.');
        window.location.reload();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur');
      }
    };

    const handleBillingControl = async (reservationId, action) => {
      let body = { action };
      if (action === 'postpone') {
        const until = window.prompt('Reporter le prochain prélèvement à quelle date ? (format AAAA-MM-JJ)');
        if (!until) return;
        body.until = until.trim();
      } else if (action === 'pause') {
        if (!window.confirm('Suspendre les prélèvements de cet abonnement ? Aucun montant ne sera débité tant que vous ne réactivez pas.')) return;
      } else if (action === 'resume') {
        if (!window.confirm('Réactiver les prélèvements de cet abonnement ?')) return;
      }
      try {
        const res = await axios.post(`${API}/admin/orders/${reservationId}/billing-control`, body, { withCredentials: true });
        toast.success(res.data?.message || 'Facturation mise à jour');
        window.location.reload();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour de la facturation');
      }
    };


    const CARRIER_OPTIONS = ['Dachser', 'Chronopost', 'Colissimo', 'GLS', 'DHL', 'UPS', 'TNT', 'Geodis', 'Autre'];

    const openReturnShipping = (res) => {
      setReturnShippingOrder(res);
      const existing = returnsToProcess.find(r => r.reservation_id === res.id);
      const prevCarrier = existing?.return_carrier || '';
      if (prevCarrier && CARRIER_OPTIONS.includes(prevCarrier)) {
        setReturnCarrier(prevCarrier);
        setReturnCarrierOther('');
      } else if (prevCarrier) {
        setReturnCarrier('Autre');
        setReturnCarrierOther(prevCarrier);
      } else {
        setReturnCarrier('Dachser');
        setReturnCarrierOther('');
      }
      setReturnTracking(existing?.return_tracking_number || '');
      setReturnSendEmail(false);
      setReturnLabelUrl(existing?.return_label_url || '');
    };

    const submitReturnShipping = async () => {
      if (!returnShippingOrder) return;
      const carrier = returnCarrier === 'Autre' ? returnCarrierOther.trim() : returnCarrier;
      if (!carrier) { toast.error('Veuillez indiquer le transporteur du retour'); return; }
      if (!returnTracking.trim()) { toast.error('Veuillez saisir le numéro de suivi du retour'); return; }
      if (returnSendEmail && !returnLabelUrl.trim()) { toast.error('Un lien de bordereau (URL PDF) est requis pour l\'envoi au client'); return; }
      setReturnShippingLoading(true);
      try {
        await axios.post(`${API}/admin/orders/${returnShippingOrder.id}/process-return-shipping`, {
          carrier,
          tracking_number: returnTracking.trim(),
          send_email: returnSendEmail,
          label_url: returnLabelUrl.trim(),
        }, { withCredentials: true });
        toast.success(returnSendEmail ? 'Retour traité — bordereau envoyé au client' : 'Transporteur du retour enregistré — cliquez sur « Reçu » à l\'arrivée du colis');
        setReturnShippingOrder(null);
        try {
          const response = await axios.get(`${API}/admin/returns-to-process`, { withCredentials: true });
          setReturnsToProcess(response.data || []);
        } catch { /* ignore refresh error */ }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors du traitement du retour');
      } finally {
        setReturnShippingLoading(false);
      }
    };

    const handleMarkReturnReceived = async (res) => {
      const rr = getActiveReturn(res);
      if (!rr) return;
      const ref = res.order_number || res.id?.slice(0, 8);
      if (!window.confirm(`Confirmer la réception du colis retour de la commande ${ref} ?\nLa commande sera déplacée dans l'onglet « Remise en stock ».`)) return;
      try {
        await axios.post(`${API}/admin/return-requests/${rr.id}/mark-received`, {}, { withCredentials: true });
        toast.success('Colis marqué comme reçu — déplacé vers « Remise en stock »');
        try {
          const response = await axios.get(`${API}/admin/returns-to-process`, { withCredentials: true });
          setReturnsToProcess(response.data || []);
        } catch { /* ignore refresh error */ }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors du marquage « Reçu »');
      }
    };

    // ---- Refonte : info compacte + règle « 1 action principale + menu … » ----
    const InfoBadge = ({ icon: Icon, label, value, tone = 'slate', testid }) => {
      const tones = {
        lime: 'border-[#DFFF00]/30 bg-[#DFFF00]/5 text-slate-200',
        purple: 'border-purple-500/30 bg-purple-500/10 text-purple-200',
        emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
        amber: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
        slate: 'border-slate-700 bg-slate-800/60 text-slate-300',
      };
      return (
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] max-w-full ${tones[tone] || tones.slate}`} data-testid={testid}>
          {Icon && <Icon className="w-3 h-3 shrink-0" />}
          {label && <span className="text-slate-500 shrink-0">{label}</span>}
          <span className="font-medium truncate">{value}</span>
        </div>
      );
    };

    const renderOrderInfo = (res) => {
      const cat = categorizeOrder(res);
      const rr = getActiveReturn(res);
      const items = [];
      if (res.rental_start_date) items.push(<InfoBadge key="loc" icon={Calendar} label="Loc." value={formatDate(res.rental_start_date)} tone="lime" testid={`rental-date-${res.id?.slice(0, 8)}`} />);
      if (cat === 'expedie' && !rr) {
        const ref = res.effective_delivery_date || res.shipping?.shipped_at || res.processed_at;
        if (res.shipping?.shipped_at || res.processed_at) items.push(<InfoBadge key="ship" icon={Truck} label="Expédié" value={formatDate(res.shipping?.shipped_at || res.processed_at)} testid={`shipped-date-${res.id?.slice(0, 8)}`} />);
        if (ref) { const days = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000); items.push(<InfoBadge key="days" icon={Calendar} label="Chez client" value={days < 0 ? 'à venir' : `${days} j`} tone="lime" testid={`days-with-screen-${res.id?.slice(0, 8)}`} />); }
        if (res.shipping?.tracking_number) items.push(<InfoBadge key="trk" icon={Truck} label={res.shipping.carrier || 'Suivi'} value={res.shipping.tracking_number} testid={`tracking-info-${res.id?.slice(0, 8)}`} />);
      }
      if (res.status === 'delivered' && !rr && res.delivered_at) items.push(<InfoBadge key="deliv" icon={CheckCircle} label="Livré" value={formatDate(res.delivered_at)} tone="emerald" />);
      if (rr) {
        items.push(<InfoBadge key="resil" icon={RotateCcw} value={`Résil. ${rr.created_at ? `le ${formatDate(rr.created_at)}` : ''}`} tone="purple" testid={`return-req-info-${res.id?.slice(0, 8)}`} />);
        if (rr.admin_initiated) items.push(<InfoBadge key="fee" value="Frais : N/A (admin)" testid={`return-fee-status-${res.id?.slice(0, 8)}`} />);
        else if (rr.return_method === 'self_dropoff') items.push(<InfoBadge key="fee" value="Dépôt client (0€)" tone="emerald" testid={`return-fee-status-${res.id?.slice(0, 8)}`} />);
        else if (rr.payment_status === 'paid') items.push(<InfoBadge key="fee" value={`Frais payés${rr.return_fee ? ` ${Number(rr.return_fee).toFixed(0)}€` : ''}`} tone="emerald" testid={`return-fee-status-${res.id?.slice(0, 8)}`} />);
        else items.push(<InfoBadge key="fee" value="Frais non payés" tone="amber" testid={`return-fee-status-${res.id?.slice(0, 8)}`} />);
        if (rr.return_tracking_number) items.push(<InfoBadge key="rtrk" icon={Truck} label="Suivi retour" value={`${rr.return_carrier || '—'} · ${rr.return_tracking_number}`} testid={`return-tracking-${res.id?.slice(0, 8)}`} />);
      }
      return <div className="flex flex-col items-start gap-1.5">{items.length ? items : <span className="text-xs text-slate-600">—</span>}</div>;
    };

    const renderRowActions = (res) => {
      const cat = categorizeOrder(res);
      const rr = getActiveReturn(res);
      const restockReturn = returnsToProcess.find(r => r.reservation_id === res.id && ['label_sent', 'in_transit'].includes(r.status));
      let primary = null;
      if (res.status === 'pending_validation') {
        primary = { label: 'Dossier', icon: Eye, onClick: () => viewClientDossier(res), testid: `dossier-btn-${res.id?.slice(0, 8)}`, cls: 'bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30' };
      } else if (cat === 'remise_en_stock' || restockReturn) {
        const targetRid = (restockReturn || rr)?.id;
        primary = { label: 'Réceptionner', icon: PackageCheck, onClick: () => { if (targetRid) navigate(`/admin/returns/${targetRid}/process`); }, testid: `receive-return-btn-${res.id?.slice(0, 8)}`, cls: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30' };
      } else if (cat === 'retours' && rr) {
        primary = { label: 'Traiter le retour', icon: RotateCcw, onClick: () => openReturnShipping(res), testid: `process-return-btn-${res.id?.slice(0, 8)}`, cls: 'bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30' };
      } else if (cat === 'en_cours' && ['confirmed', 'paid', 'pending'].includes(res.status)) {
        primary = { label: 'Traiter', icon: PackageCheck, onClick: () => navigate(`/admin/orders/${res.id}/process`), testid: `process-btn-${res.id?.slice(0, 8)}`, cls: 'bg-[#DFFF00] hover:bg-[#c8e600] text-black' };
      } else if (res.status === 'shipped') {
        primary = { label: 'Livré', icon: CheckCircle, onClick: () => handleMarkDelivered(res.id), testid: `delivered-btn-${res.id?.slice(0, 8)}`, cls: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30' };
      }

      const sec = [];
      if (res.status === 'pending_validation') {
        sec.push({ label: 'Approuver', icon: CheckCircle2, onClick: () => handleApproveBooking(res.id), testid: `approve-btn-${res.id?.slice(0, 8)}` });
        sec.push({ label: 'Refuser', icon: X, onClick: () => handleRejectBooking(res.id), testid: `reject-btn-${res.id?.slice(0, 8)}`, danger: true });
      }
      if (cat === 'retours' && rr) sec.push({ label: 'Marquer « Reçu »', icon: PackageCheck, onClick: () => handleMarkReturnReceived(res), testid: `mark-received-btn-${res.id?.slice(0, 8)}` });
      if (cat === 'remise_en_stock') sec.push({ label: 'Modifier transporteur retour', icon: Truck, onClick: () => openReturnShipping(res), testid: `edit-return-shipping-btn-${res.id?.slice(0, 8)}` });
      sec.push({ label: 'Voir les détails', icon: Eye, onClick: () => viewOrderDetails(res), testid: `view-details-btn-${res.id?.slice(0, 8)}` });
      sec.push({ label: 'Modifier le statut', icon: SlidersHorizontal, onClick: () => openStatusEditor(res), testid: `edit-status-btn-${res.id?.slice(0, 8)}` });
      sec.push({ label: 'Modifier le pack', icon: Package, onClick: () => openEditPack(res), testid: `rename-pack-btn-${res.id?.slice(0, 8)}` });
      if (res.has_rc_pro) sec.push({ label: 'Attestation RC Pro', icon: Shield, onClick: () => window.open(`${API}/admin/orders/${res.id}/rc-pro`, '_blank'), testid: `view-rc-pro-btn-${res.id?.slice(0, 8)}` });
      if (['shipped', 'delivered', 'active'].includes(res.status) && !hasActiveReturn(res)) sec.push({ label: 'Résilier la location', icon: Ban, onClick: () => { setTerminatingOrder(res); setTerminateReason(''); }, testid: `terminate-rental-btn-${res.id?.slice(0, 8)}`, danger: true });
      if (res.payment_status === 'paid' && !['cancelled', 'rejected', 'terminated', 'fully_refunded'].includes(res.status) && res.full_refund_status !== 'completed') sec.push({ label: 'Remboursement intégral', icon: Undo2, onClick: () => { setRefundingOrder(res); setRefundPassword(''); setRefundReason('Rétractation client'); }, testid: `refund-full-btn-${res.id?.slice(0, 8)}`, danger: true });
      if (!hasActiveReturn(res)) sec.push({ label: 'Déplacer dans la corbeille', icon: Trash2, onClick: () => { setDeletingOrder(res); setDeletePassword(''); setDeleteMode('soft'); }, testid: `delete-order-btn-${res.id?.slice(0, 8)}`, danger: true });

      const PIcon = primary?.icon;
      return (
        <div className="flex items-center justify-end gap-2">
          {primary && (
            <Button size="sm" className={`${primary.cls} text-xs whitespace-nowrap`} onClick={primary.onClick} data-testid={primary.testid}>
              {PIcon && <PIcon className="w-4 h-4 mr-1" />}{primary.label}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-slate-700 text-slate-300 hover:bg-slate-800" title="Plus d'actions" data-testid={`row-menu-${res.id?.slice(0, 8)}`}>
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-slate-900 border border-slate-800 text-slate-200 min-w-[220px]">
              {sec.map((a, i) => {
                const AIcon = a.icon;
                return (
                  <DropdownMenuItem key={a.testid || i} onClick={a.onClick} data-testid={a.testid} className={`gap-2 cursor-pointer ${a.danger ? 'text-red-400 focus:text-red-300 focus:bg-red-500/10' : 'focus:bg-slate-800'}`}>
                    {AIcon && <AIcon className="w-4 h-4" />}{a.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    };

    const handleConfirmFullRefund = async () => {
      if (!refundingOrder) return;
      if (!refundPassword) {
        toast.error('Mot de passe requis');
        return;
      }
      setRefundLoading(true);
      try {
        const { data } = await axios.post(
          `${API}/admin/orders/${refundingOrder.id}/refund-full`,
          { password: refundPassword, reason: refundReason || 'Rétractation client' },
          { withCredentials: true }
        );
        toast.success(data.message || 'Remboursement intégral effectué.');
        setRefundingOrder(null);
        setRefundPassword('');
        setRefundReason('Rétractation client');
        window.location.reload();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors du remboursement');
      } finally {
        setRefundLoading(false);
      }
    };

    const handleConfirmDeleteOrder = async () => {
      if (!deletingOrder) return;
      if (!deletePassword) {
        toast.error('Mot de passe requis');
        return;
      }
      setDeleteLoading(true);
      try {
        if (deleteMode === 'permanent') {
          const { data } = await axios.delete(
            `${API}/admin/orders/${deletingOrder.id}/permanent`,
            { data: { password: deletePassword }, withCredentials: true }
          );
          toast.success(data.message || 'Commande supprimée définitivement');
          setDeletingOrder(null);
          setDeletePassword('');
          setDeleteMode('soft');
          fetchTrash();
        } else {
          const { data } = await axios.delete(
            `${API}/admin/orders/${deletingOrder.id}`,
            { data: { password: deletePassword }, withCredentials: true }
          );
          toast.success(data.message || 'Commande déplacée dans la corbeille');
          setDeletingOrder(null);
          setDeletePassword('');
          window.location.reload();
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
      } finally {
        setDeleteLoading(false);
      }
    };

    const fetchTrash = async () => {
      setTrashLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/orders/trash`, { withCredentials: true });
        setTrashItems(data || []);
      } catch (error) {
        toast.error('Erreur lors du chargement de la corbeille');
      } finally {
        setTrashLoading(false);
      }
    };

    const openTrash = () => { setActiveTab('corbeille'); fetchTrash(); };

    const handleRestoreOrder = async (item) => {
      try {
        const { data } = await axios.post(
          `${API}/admin/orders/${item.id}/restore`, {}, { withCredentials: true }
        );
        toast.success(data.message || 'Commande restaurée');
        window.location.reload();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
      }
    };

    const handleSaveEditPack = async () => {
      if (!editingPack) return;
      const name = (editingPack.pack_name || '').trim();
      if (!name) { toast.error('Le nom du pack est requis'); return; }
      setEditPackLoading(true);
      try {
        await axios.put(
          `${API}/admin/orders/${editingPack.id}/pack`,
          {
            pack_name: name,
            pack_surface: (editingPack.pack_surface || '').trim(),
            pack_id: editingPack.pack_id || '',
          },
          { withCredentials: true }
        );
        toast.success(editingPack.pack_id ? 'Pack renommé et lié au catalogue (préparation OK)' : 'Pack renommé (pack custom)');
        setEditingPack(null);
        window.location.reload();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors du renommage');
      } finally {
        setEditPackLoading(false);
      }
    };

    const loadCatalogPacks = async () => {
      if (catalogPacks.length > 0) return;
      try {
        const { data } = await axios.get(`${API}/admin/stock`, { withCredentials: true });
        if (Array.isArray(data)) setCatalogPacks(data);
      } catch (e) {
        // Silent — admin can still rename without linking
      }
    };

    const openEditPack = (res) => {
      setEditingPack({
        id: res.id,
        pack_name: res.pack_name || '',
        pack_surface: res.pack_surface || '',
        pack_id: res.pack_id || '',
      });
      loadCatalogPacks();
    };
    
    // Fetch returns to process
    useEffect(() => {
      const fetchReturns = async () => {
        try {
          const response = await axios.get(`${API}/admin/returns-to-process`, { withCredentials: true });
          setReturnsToProcess(response.data || []);
        } catch (error) {
          console.error('Error fetching returns to process:', error);
        }
      };
      fetchReturns();
    }, []);
    
    const viewClientDossier = async (order) => {
      setClientDossier(order);
      setLoadingDossier(true);
      try {
        // Fetch RC document
        const rcResponse = await axios.get(`${API}/admin/orders/${order.id}/rc-document`, {
          withCredentials: true
        });
        setClientDossier(prev => ({ ...prev, rcDocument: rcResponse.data }));
      } catch (error) {
        console.error('Error fetching RC document:', error);
        // Document may not exist, continue without it
        setClientDossier(prev => ({ ...prev, rcDocument: null }));
      } finally {
        setLoadingDossier(false);
      }
    };
    
    const viewOrderDetails = async (order) => {
      setSelectedOrder(order);
      setLoadingDetails(true);
      try {
        const response = await axios.get(`${API}/admin/orders/${order.id}/details`, {
          withCredentials: true
        });
        setOrderDetails(response.data);
      } catch (error) {
        console.error('Error fetching order details:', error);
        toast.error('Erreur lors du chargement des détails');
      } finally {
        setLoadingDetails(false);
      }
    };
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Commandes</h2>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Rechercher..." className="pl-10 bg-slate-800 border-slate-700 text-white w-64" />
            </div>
            <Button variant="outline" size="sm" className="border-slate-700 text-slate-300">
              <Filter className="w-4 h-4 mr-2" />
              Filtrer
            </Button>
            <Button variant="outline" size="sm" className="border-slate-700 text-slate-300">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Pending Validation Alert */}
        {pendingValidation.length > 0 && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-400" />
              <div>
                <p className="font-semibold text-orange-400">{pendingValidation.length} commande(s) en attente de validation</p>
                <p className="text-sm text-slate-400">Ces commandes ont été payées et nécessitent votre approbation</p>
              </div>
            </div>
          </div>
        )}

        {/* SEPA Pending Approvals — Banner with quick-review buttons */}
        {sepaPending.length > 0 && (
          <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-xl p-4" data-testid="orders-sepa-pending-banner">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <Building2 className="w-6 h-6 text-yellow-400" />
                <div>
                  <p className="font-semibold text-yellow-300">
                    {sepaPending.length} demande{sepaPending.length > 1 ? 's' : ''} de prélèvement SEPA à valider
                  </p>
                  <p className="text-sm text-slate-400">Examinez le dossier (RC Pro, contrat, IBAN) avant d'approuver le 1er prélèvement</p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {sepaPending.map(s => (
                <div
                  key={s.id}
                  className="flex items-center justify-between gap-3 bg-slate-900/60 rounded-lg px-3 py-2 hover:bg-slate-900/80 transition cursor-pointer"
                  onClick={() => setSepaDetailId(s.id)}
                  data-testid={`orders-sepa-row-${s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium truncate">{s.customer_name}</span>
                      <span className="text-xs text-slate-500 truncate">· {s.pack_name}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono">**** {s.iban_last4 || '????'} · 1er prélèvement {formatPrice(s.first_charge_amount || 0)}</div>
                  </div>
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setSepaDetailId(s.id); }}
                    className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 border border-yellow-500/40 text-xs"
                    data-testid={`orders-sepa-review-${s.id}`}
                  >
                    <Eye className="w-3 h-3 mr-1" /> Examiner le dossier
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SEPA detail modal — shared between Orders and SEPA admin */}
        {sepaDetailId && (
          <SEPARequestDetailModal
            requestId={sepaDetailId}
            onClose={() => setSepaDetailId(null)}
            onChanged={fetchPendingSEPA}
          />
        )}

        {/* Ready to Process Alert */}
        {confirmedOrders.length > 0 && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <PackageCheck className="w-6 h-6 text-blue-400" />
              <div>
                <p className="font-semibold text-blue-400">{confirmedOrders.length} commande(s) à traiter</p>
                <p className="text-sm text-slate-400">Ces commandes sont approuvées et prêtes à être préparées pour expédition</p>
              </div>
            </div>
          </div>
        )}

        {/* Returns to Process Alert */}
        {returnsToProcess.filter(r => r.status === 'label_sent').length > 0 && (
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RotateCcw className="w-6 h-6 text-purple-400" />
                <div>
                  <p className="font-semibold text-purple-400">{returnsToProcess.filter(r => r.status === 'label_sent').length} retour(s) à réceptionner</p>
                  <p className="text-sm text-slate-400">Bordereaux envoyés - En attente de réception du matériel</p>
                </div>
              </div>
              <div className="flex gap-2">
                {returnsToProcess.filter(r => r.status === 'label_sent').slice(0, 3).map(ret => (
                  <Button 
                    key={ret.id}
                    size="sm"
                    onClick={() => navigate(`/admin/returns/${ret.id}/process`)}
                    className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30"
                  >
                    <Scan className="w-4 h-4 mr-1" />
                    {ret.customer?.lastName || 'Client'}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Onglets par étape du cycle + Corbeille */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Button
            size="sm"
            onClick={() => setActiveTab('en_cours')}
            className={activeTab === 'en_cours' ? 'bg-[#DFFF00] text-black hover:bg-[#c5e600]' : 'border border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800'}
            data-testid="orders-tab-en-cours"
          >
            En cours{tabCounts.en_cours ? ` (${tabCounts.en_cours})` : ''}
          </Button>
          <Button
            size="sm"
            onClick={() => setActiveTab('expedie')}
            className={activeTab === 'expedie' ? 'bg-blue-500 text-white hover:bg-blue-600' : 'border border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800'}
            data-testid="orders-tab-expedie"
          >
            Expédiées / Livrées{tabCounts.expedie ? ` (${tabCounts.expedie})` : ''}
          </Button>
          <Button
            size="sm"
            onClick={() => setActiveTab('retours')}
            className={`relative ${activeTab === 'retours' ? 'bg-purple-500 text-white hover:bg-purple-600' : 'border border-purple-500/40 bg-transparent text-purple-300 hover:bg-purple-500/10'}`}
            data-testid="orders-tab-retours"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Résiliation
            {tabCounts.retours > 0 && (
              <span
                className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold shadow-[0_0_0_2px_rgba(239,68,68,0.35)] animate-pulse"
                data-testid="resiliation-notif-badge"
              >
                {tabCounts.retours}
              </span>
            )}
          </Button>
          <Button
            size="sm"
            onClick={() => setActiveTab('remise_en_stock')}
            className={activeTab === 'remise_en_stock' ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'border border-emerald-500/40 bg-transparent text-emerald-300 hover:bg-emerald-500/10'}
            data-testid="orders-tab-remise-en-stock"
          >
            <PackageCheck className="w-4 h-4 mr-1" />
            Remise en stock{tabCounts.remise_en_stock ? ` (${tabCounts.remise_en_stock})` : ''}
          </Button>
          <Button
            size="sm"
            onClick={() => setActiveTab('archive')}
            className={activeTab === 'archive' ? 'bg-slate-500 text-white hover:bg-slate-600' : 'border border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800'}
            data-testid="orders-tab-archive"
          >
            Archivées{tabCounts.archive ? ` (${tabCounts.archive})` : ''}
          </Button>
          <Button
            size="sm"
            onClick={openTrash}
            className={showTrash ? 'bg-red-500 text-white hover:bg-red-600' : 'border border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10'}
            data-testid="orders-trash-tab"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Corbeille{trashItems.length ? ` (${trashItems.length})` : ''}
          </Button>
        </div>

        {/* Indication contextuelle pour l'onglet Retours */}
        {activeTab === 'retours' && (
          <p className="text-xs text-purple-300/80 mb-3" data-testid="orders-retours-hint">
            Commandes pour lesquelles le client a demandé une <strong>résiliation</strong>. Cliquez sur « <strong>Traiter le retour</strong> » pour choisir le transporteur du retour et saisir le n° de suivi. La commande passera alors dans l'onglet « Remise en stock ».
          </p>
        )}

        {/* Indication contextuelle pour l'onglet Remise en stock */}
        {activeTab === 'remise_en_stock' && (
          <p className="text-xs text-emerald-300/80 mb-3" data-testid="orders-remise-hint">
            Retours <strong>en cours d'acheminement</strong> (transporteur assigné). Cliquez sur « <strong>Réceptionner retour</strong> » quand le colis arrive pour <strong>scanner les articles et les remettre en stock</strong>.
          </p>
        )}

        {/* Indication contextuelle pour l'onglet Archivées */}
        {activeTab === 'archive' && (
          <p className="text-xs text-slate-500 mb-3" data-testid="orders-archive-hint">
            Commandes dont le cycle est terminé (matériel retourné, annulées ou remboursées). Elles se rangent ici automatiquement.
          </p>
        )}

        {showTrash && (
          <div className="bg-slate-900 rounded-xl border border-red-500/20 overflow-hidden mb-4" data-testid="orders-trash-panel">
            <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
              <span className="text-sm text-slate-300 font-medium">Corbeille — commandes archivées (restaurables)</span>
              <span className="text-xs text-slate-500">{trashLoading ? 'Chargement…' : `${trashItems.length} commande(s)`}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">ID</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Client</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Pack</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Supprimée le</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Par</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {trashItems.length === 0 && !trashLoading && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500 text-sm">La corbeille est vide.</td></tr>
                  )}
                  {trashItems.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-sm text-slate-300 font-mono">{item.order_number || item.id?.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {item.customer?.firstName} {item.customer?.lastName}
                        <div className="text-xs text-slate-500">{item.customer?.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-400">{item.pack_name}</td>
                      <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{item.deleted_at ? formatDate(item.deleted_at) : '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{item.deleted_by || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 text-xs" onClick={() => handleRestoreOrder(item)} data-testid={`restore-order-btn-${item.id?.slice(0, 8)}`}>
                            <Undo2 className="w-3 h-3 mr-1" />
                            Restaurer
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs px-2" onClick={() => { setDeletingOrder(item); setDeletePassword(''); setDeleteMode('permanent'); }} title="Supprimer définitivement" data-testid={`permanent-delete-btn-${item.id?.slice(0, 8)}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className={`bg-slate-900 rounded-xl border border-slate-800 overflow-hidden ${showTrash ? 'hidden' : ''}`}>
          <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <colgroup>
              <col style={{ width: '13%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '26%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '9%' }} />
            </colgroup>
            <thead className="bg-slate-800/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-400">Commande</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-400">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-400">Pack & Mensualité</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-400">Infos</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-400">Statut</th>
                <th className="text-right px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredReservations.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500 text-sm" data-testid="orders-empty-row">Aucune commande dans cette catégorie.</td></tr>
              )}
              {filteredReservations.map((res) => (
                <tr key={res.id} className={`hover:bg-slate-800/30 transition-colors ${res.status === 'pending_validation' ? 'bg-orange-500/5' : res.status === 'confirmed' ? 'bg-blue-500/5' : ''}`}>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-mono text-slate-200" title={res.id}>{res.order_number || res.id?.slice(0, 8)}</span>
                      {(res.is_manual_order || res.source === 'manual_order') && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-purple-500/20 text-purple-400 border border-purple-500/30" title="Commande manuelle"><UserPlus className="w-3 h-3" />Manuel</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">{formatDate(res.created_at)}</div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <p className="text-sm text-white truncate">{res.customer?.firstName} {res.customer?.lastName}</p>
                    <p className="text-xs text-slate-500 truncate">{res.customer?.email}</p>
                    {res.seller_name && (<p className="text-[11px] text-purple-400 flex items-center gap-1 truncate"><UserPlus className="w-3 h-3 shrink-0" />{res.seller_name}</p>)}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <p className="text-sm text-white truncate"><PackName name={res.pack_name} /></p>
                    <p className="text-xs text-slate-500">{res.pack_surface}</p>
                    <p className="text-sm text-[#DFFF00] font-medium mt-0.5">{res.monthly_price != null ? `${formatPrice(res.monthly_price)}/mois` : '—'}</p>
                  </td>
                  <td className="px-4 py-3 align-top">{renderOrderInfo(res)}</td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-col gap-1.5 items-start">
                      {getStatusBadge(res.status)}
                      {(() => { const ps = res.payment_status || 'pending'; const badge = PAYMENT_STATUS_BADGES[ps] || PAYMENT_STATUS_BADGES.pending; return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.color}`} data-testid={`payment-badge-${res.id?.slice(0, 8)}`}>{badge.text}</span>; })()}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">{renderRowActions(res)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        
        {/* Order Details Modal */}
        {selectedOrder && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/20 flex items-center justify-center">
                    <ShoppingCart className="w-5 h-5 text-[#DFFF00]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Détails de la commande</h2>
                    <p className="text-sm text-slate-400">#{selectedOrder.id?.slice(0, 8)} - <PackName name={selectedOrder.pack_name} /></p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedOrder(null); setOrderDetails(null); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-[#DFFF00]" />
                  </div>
                ) : (
                  <>
                    {/* Status & Info Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Client */}
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <User className="w-4 h-4 text-[#DFFF00]" />
                          <span className="text-sm font-medium text-slate-400">Client</span>
                        </div>
                        <p className="text-white font-medium">{selectedOrder.customer?.firstName} {selectedOrder.customer?.lastName}</p>
                        {selectedOrder.customer?.company && (
                          <p className="text-slate-400 text-sm">{selectedOrder.customer.company}</p>
                        )}
                        {selectedOrder.customer?.siren && (
                          <p className="text-slate-500 text-xs">SIREN: {selectedOrder.customer.siren}</p>
                        )}
                        {selectedOrder.customer?.activityType && (
                          <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full border ${getActivityTypeBadgeClass(selectedOrder.customer.activityType)}`}>
                            {formatActivityType(selectedOrder.customer.activityType)}
                          </span>
                        )}
                        <p className="text-slate-400 text-sm mt-1">{selectedOrder.customer?.email}</p>
                        <p className="text-slate-400 text-sm">{selectedOrder.customer?.phone}</p>
                        <p className="text-slate-400 text-sm mt-1">{selectedOrder.customer?.address}</p>
                        <p className="text-slate-400 text-sm">{selectedOrder.customer?.postalCode} {selectedOrder.customer?.city}</p>
                      </div>
                      
                      {/* Paiement */}
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <CreditCard className="w-4 h-4 text-[#DFFF00]" />
                          <span className="text-sm font-medium text-slate-400">Paiement</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Abonnement</span>
                            <span className="text-white font-medium">{formatPrice(selectedOrder.monthly_price)}/mois</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Caution</span>
                            <span className="text-white font-medium">{formatPrice(selectedOrder.caution)}</span>
                          </div>
                          {selectedOrder.next_billing_date_human && (
                            <div className="flex justify-between">
                              <span className="text-slate-400">Prochain prélèvement</span>
                              <span className="text-white font-medium">{selectedOrder.next_billing_date_human}</span>
                            </div>
                          )}
                          {selectedOrder.billing_hold_applied && !selectedOrder.delivered_at && (
                            <div className="text-xs text-amber-400 mt-1" data-testid="billing-hold-badge">
                              ⏳ Prélèvement en attente de livraison
                            </div>
                          )}
                          {selectedOrder.subscription_paused && (
                            <div className="text-xs text-red-400 mt-1" data-testid="billing-paused-badge">
                              ⏸ Prélèvements suspendus
                            </div>
                          )}
                          <div className="pt-2 border-t border-slate-700 mt-2">
                            <p className="text-xs text-slate-500">Session: {selectedOrder.stripe_session_id?.slice(0, 20)}...</p>
                          </div>
                          {selectedOrder.stripe_subscription_id && (
                            <div className="flex flex-wrap gap-2 pt-2">
                              {selectedOrder.subscription_paused ? (
                                <Button size="sm" variant="outline" className="text-xs h-7 text-emerald-400 border-emerald-500/40"
                                  onClick={() => handleBillingControl(selectedOrder.id, 'resume')}
                                  data-testid="billing-resume-btn">
                                  Réactiver les prélèvements
                                </Button>
                              ) : (
                                <Button size="sm" variant="outline" className="text-xs h-7 text-red-400 border-red-500/40"
                                  onClick={() => handleBillingControl(selectedOrder.id, 'pause')}
                                  data-testid="billing-pause-btn">
                                  Suspendre les prélèvements
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Statut */}
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Package className="w-4 h-4 text-[#DFFF00]" />
                          <span className="text-sm font-medium text-slate-400">Statut</span>
                        </div>
                        <div className="mb-2">{getStatusBadge(selectedOrder.status)}</div>
                        <p className="text-sm text-slate-400">Créé le {formatDate(selectedOrder.created_at)}</p>
                        {selectedOrder.processed_at && (
                          <p className="text-sm text-slate-400">Traité le {formatDate(selectedOrder.processed_at)}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Shipping Info (if shipped) */}
                    {selectedOrder.shipping && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Truck className="w-5 h-5 text-green-400" />
                          <span className="font-medium text-green-400">Expédition</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-400">Transporteur</p>
                            <p className="text-white font-medium">{selectedOrder.shipping.carrier}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">N° de suivi</p>
                            <p className="text-[#DFFF00] font-mono">{selectedOrder.shipping.tracking_number}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Expédié le</p>
                            <p className="text-white">{formatDate(selectedOrder.shipping.shipped_at)}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Par</p>
                            <p className="text-white">{selectedOrder.shipping.shipped_by}</p>
                          </div>
                        </div>
                        {selectedOrder.shipping.tracking_url && (
                          <a 
                            href={selectedOrder.shipping.tracking_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 mt-3 text-[#DFFF00] hover:underline text-sm"
                          >
                            Suivre le colis <ChevronRight className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    )}
                    
                    {/* Assigned Items (if any) */}
                    {selectedOrder.assigned_items && selectedOrder.assigned_items.length > 0 && (
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <ClipboardCheck className="w-5 h-5 text-[#DFFF00]" />
                          <span className="font-medium text-white">Articles assignés ({selectedOrder.assigned_items.length})</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-700">
                                <th className="text-left py-2 text-slate-400 font-medium">Article</th>
                                <th className="text-left py-2 text-slate-400 font-medium">N° Série</th>
                                <th className="text-left py-2 text-slate-400 font-medium">État</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedOrder.assigned_items.map((item, idx) => (
                                <tr key={idx} className="border-b border-slate-800">
                                  <td className="py-2 text-white">{item.product_name}</td>
                                  <td className="py-2 text-[#DFFF00] font-mono">{item.serial_number}</td>
                                  <td className="py-2">
                                    <span className={`px-2 py-1 rounded text-xs ${
                                      item.is_new_at_departure 
                                        ? 'bg-green-500/20 text-green-400' 
                                        : 'bg-yellow-500/20 text-yellow-400'
                                    }`}>
                                      {item.is_new_at_departure ? 'Neuf' : 'Occasion'} - {item.condition_at_departure}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* Pack Components (if not shipped yet) */}
                    {orderDetails?.pack_components && orderDetails.pack_components.length > 0 && !selectedOrder.assigned_items?.length && (
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Package className="w-5 h-5 text-[#DFFF00]" />
                          <span className="font-medium text-white">Contenu du pack</span>
                        </div>
                        <div className="space-y-2">
                          {orderDetails.pack_components.map((comp, idx) => (
                            <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                              <span className="text-slate-300">{comp.product_name}</span>
                              <span className="text-white font-medium">{comp.quantity}x</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-slate-800 flex justify-end gap-3">
                {selectedOrder.status === 'confirmed' && (
                  <Button 
                    className="bg-[#DFFF00] hover:bg-[#c8e600] text-black"
                    onClick={() => { setSelectedOrder(null); navigate(`/admin/orders/${selectedOrder.id}/process`); }}
                  >
                    <PackageCheck className="w-4 h-4 mr-2" />
                    Traiter la commande
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className="border-slate-700 text-slate-300"
                  onClick={() => { setSelectedOrder(null); setOrderDetails(null); }}
                >
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Pack Modal — primary use: change preparation pack (which items to scan).
            Renaming the displayed label is optional. */}
        {editingPack && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="edit-pack-modal">
            <div className="bg-slate-900 rounded-xl w-full max-w-md overflow-hidden border border-slate-700">
              <div className="flex items-center justify-between p-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-[#DFFF00]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Pack à préparer</h2>
                    <p className="text-xs text-slate-400">Commande #{editingPack.id?.slice(0, 8)}</p>
                  </div>
                </div>
                <button
                  onClick={() => !editPackLoading && setEditingPack(null)}
                  className="text-slate-400 hover:text-white"
                  disabled={editPackLoading}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-5">
                {/* PRIMARY: composition catalogue */}
                <div className="bg-[#DFFF00]/5 border border-[#DFFF00]/20 rounded-lg p-3">
                  <label className="text-sm font-semibold text-[#DFFF00] mb-2 flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4" />
                    Composants à scanner par les préparateurs
                  </label>
                  <select
                    value={editingPack.pack_id || ''}
                    onChange={(e) => setEditingPack(prev => ({ ...prev, pack_id: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="edit-pack-catalog-select"
                  >
                    <option value="">— Aucun (préparation libre, scan permissif) —</option>
                    {catalogPacks.map(p => (
                      <option key={p.pack_id || p.id} value={p.pack_id || p.id}>
                        {p.name} ({p.pack_id || p.id})
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-slate-500 mt-2">
                    Sélectionnez le pack catalogue dont la composition doit s'afficher dans l'écran "Traiter".
                    Ex : pour envoyer un Pack 4m², choisissez "Pack M - Compact 4m²".
                  </p>
                </div>

                {/* SECONDARY (optional): rename display label */}
                <details className="text-xs">
                  <summary className="text-slate-400 hover:text-slate-300 cursor-pointer select-none">
                    Modifier aussi l'intitulé affiché du pack (optionnel)
                  </summary>
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Nom affiché</label>
                      <Input
                        type="text"
                        value={editingPack.pack_name || ''}
                        onChange={(e) => setEditingPack(prev => ({ ...prev, pack_name: e.target.value }))}
                        placeholder="Ex: Pack 4m² sur-mesure"
                        className="bg-slate-800 border-slate-700 text-white"
                        data-testid="edit-pack-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Surface</label>
                      <Input
                        type="text"
                        value={editingPack.pack_surface || ''}
                        onChange={(e) => setEditingPack(prev => ({ ...prev, pack_surface: e.target.value }))}
                        placeholder="Ex: 4m²"
                        className="bg-slate-800 border-slate-700 text-white"
                        data-testid="edit-pack-surface-input"
                      />
                    </div>
                  </div>
                </details>
              </div>
              <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-800">
                <Button
                  variant="ghost"
                  onClick={() => setEditingPack(null)}
                  disabled={editPackLoading}
                  className="text-slate-400 hover:text-white"
                  data-testid="edit-pack-cancel-btn"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSaveEditPack}
                  disabled={editPackLoading || !editingPack.pack_name?.trim()}
                  className="bg-[#DFFF00] hover:bg-[#DFFF00]/90 text-black font-semibold"
                  data-testid="edit-pack-save-btn"
                >
                  {editPackLoading ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</>) : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Full Refund Confirmation Modal */}
        {returnShippingOrder && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="return-shipping-modal">
            <div className="bg-slate-900 rounded-xl w-full max-w-md overflow-hidden border border-purple-500/30">
              <div className="flex items-center justify-between p-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <RotateCcw className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Traiter le retour</h2>
                    <p className="text-xs text-slate-400">Commande #{returnShippingOrder.order_number || returnShippingOrder.id?.slice(0, 8)}</p>
                  </div>
                </div>
                <button
                  onClick={() => { if (!returnShippingLoading) setReturnShippingOrder(null); }}
                  className="text-slate-400 hover:text-white"
                  disabled={returnShippingLoading}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 text-xs text-purple-200">
                  Renseignez le <strong>transporteur du retour</strong> et le <strong>n° de suivi du retour</strong> (différents du n° d'envoi initial). Vous pouvez aussi envoyer le bordereau de retour au client.
                </div>

                {/* Infos client + poids + expédition (repris de la commande) */}
                {(() => {
                  const c = returnShippingOrder.customer || {};
                  const ship = returnShippingOrder.shipping || {};
                  const clientName = c.company || `${c.firstName || ''} ${c.lastName || ''}`.trim() || '—';
                  const addr = [c.address, `${c.postal_code || ''} ${c.city || ''}`.trim()].filter(Boolean).join(', ') || '—';
                  return (
                    <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3 space-y-2 text-xs" data-testid="return-shipping-client-info">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Enlèvement chez le client</div>
                      <div className="grid grid-cols-3 gap-1">
                        <span className="text-slate-500">Client</span>
                        <span className="col-span-2 text-slate-200 font-medium">{clientName}</span>
                        <span className="text-slate-500">Adresse</span>
                        <span className="col-span-2 text-slate-200">{addr}</span>
                        <span className="text-slate-500">Téléphone</span>
                        <span className="col-span-2 text-slate-200">{c.phone || '—'}</span>
                        <span className="text-slate-500">Email</span>
                        <span className="col-span-2 text-slate-200 break-all">{c.email || '—'}</span>
                        <span className="text-slate-500">Pack</span>
                        <span className="col-span-2 text-slate-200">{returnShippingOrder.pack_name || returnShippingOrder.pack_surface || '—'}</span>
                        <span className="text-slate-500">Poids</span>
                        <span className="col-span-2 text-[#DFFF00] font-semibold">{returnShippingOrder.pack_weight != null ? `${returnShippingOrder.pack_weight} kg` : 'Non renseigné'}</span>
                      </div>
                      {(ship.carrier || ship.tracking_number) && (
                        <div className="pt-2 mt-1 border-t border-slate-700 grid grid-cols-3 gap-1">
                          <span className="text-slate-500">Expédié via</span>
                          <span className="col-span-2 text-slate-300">{ship.carrier || '—'}{ship.tracking_number ? ` · ${ship.tracking_number}` : ''}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Transporteur du retour</label>
                  <select
                    value={returnCarrier}
                    onChange={(e) => setReturnCarrier(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                    data-testid="return-carrier-select"
                  >
                    {CARRIER_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {returnCarrier === 'Autre' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Nom du transporteur</label>
                    <Input
                      value={returnCarrierOther}
                      onChange={(e) => setReturnCarrierOther(e.target.value)}
                      placeholder="Ex. Heppner, Kuehne+Nagel…"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="return-carrier-other-input"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">N° de suivi du retour</label>
                  <Input
                    value={returnTracking}
                    onChange={(e) => setReturnTracking(e.target.value)}
                    placeholder="N° de suivi du colis retour"
                    className="bg-slate-800 border-slate-700 text-white font-mono"
                    data-testid="return-tracking-input"
                  />
                </div>

                <div className="border-t border-slate-800 pt-3">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={returnSendEmail}
                      onChange={(e) => setReturnSendEmail(e.target.checked)}
                      className="w-4 h-4 accent-purple-500"
                      data-testid="return-send-email-checkbox"
                    />
                    Envoyer le bordereau de retour au client par email
                  </label>
                  {returnSendEmail && (
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-slate-400 mb-1">Lien du bordereau (URL PDF)</label>
                      <Input
                        value={returnLabelUrl}
                        onChange={(e) => setReturnLabelUrl(e.target.value)}
                        placeholder="https://…/bordereau.pdf"
                        className="bg-slate-800 border-slate-700 text-white"
                        data-testid="return-label-url-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 p-4 border-t border-slate-800">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  onClick={() => setReturnShippingOrder(null)}
                  disabled={returnShippingLoading}
                  data-testid="return-shipping-cancel-btn"
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
                  onClick={submitReturnShipping}
                  disabled={returnShippingLoading}
                  data-testid="return-shipping-submit-btn"
                >
                  {returnShippingLoading ? 'Traitement…' : (returnSendEmail ? 'Enregistrer & envoyer' : 'Enregistrer le retour')}
                </Button>
              </div>
            </div>
          </div>
        )}


        {refundingOrder && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="refund-full-modal">
            <div className="bg-slate-900 rounded-xl w-full max-w-md overflow-hidden border border-red-500/30">
              <div className="flex items-center justify-between p-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <Undo2 className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Remboursement intégral</h2>
                    <p className="text-xs text-slate-400">Commande #{refundingOrder.id?.slice(0, 8)}</p>
                  </div>
                </div>
                <button
                  onClick={() => { if (!refundLoading) { setRefundingOrder(null); setRefundPassword(''); } }}
                  className="text-slate-400 hover:text-white"
                  disabled={refundLoading}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">
                  <p className="font-semibold mb-1">⚠️ Action irréversible</p>
                  <p className="text-xs">
                    Cette opération rembourse <strong>l'intégralité</strong> des montants prélevés sur Stripe pour ce client
                    (1<sup>er</sup> mois, caution, frais de transport et éventuels prélèvements récurrents).
                    L'abonnement Stripe sera annulé et le client recevra un email de confirmation.
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-slate-400">Client</p>
                  <p className="text-white">{refundingOrder.customer?.firstName} {refundingOrder.customer?.lastName} — {refundingOrder.customer?.email}</p>
                  <p className="text-xs text-slate-400 mt-2">Pack</p>
                  <p className="text-white">{refundingOrder.pack_name}</p>
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-slate-400">Motif (optionnel)</label>
                  <Input
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="Rétractation client"
                    className="bg-slate-800 border-slate-700 text-white"
                    disabled={refundLoading}
                    data-testid="refund-full-reason-input"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-slate-400">Mot de passe administrateur <span className="text-red-400">*</span></label>
                  <Input
                    type="password"
                    value={refundPassword}
                    onChange={(e) => setRefundPassword(e.target.value)}
                    placeholder="Mot de passe requis"
                    className="bg-slate-800 border-slate-700 text-white"
                    disabled={refundLoading}
                    autoFocus
                    data-testid="refund-full-password-input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-800">
                <Button
                  variant="ghost"
                  onClick={() => { setRefundingOrder(null); setRefundPassword(''); }}
                  disabled={refundLoading}
                  className="text-slate-400 hover:text-white"
                  data-testid="refund-full-cancel-btn"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleConfirmFullRefund}
                  disabled={refundLoading || !refundPassword}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="refund-full-confirm-btn"
                >
                  {refundLoading ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Remboursement...</>) : (<><Undo2 className="w-4 h-4 mr-2" />Confirmer le remboursement</>)}
                </Button>
              </div>
            </div>
          </div>
        )}


        {/* Delete Order Modal */}
        {terminatingOrder && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="terminate-rental-modal">
            <div className="bg-slate-900 rounded-xl w-full max-w-md overflow-hidden border border-red-500/30">
              <div className="flex items-center justify-between p-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <Ban className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Résilier la location</h2>
                    <p className="text-xs text-slate-400">Commande #{terminatingOrder.order_number || terminatingOrder.id?.slice(0, 8)} — {(terminatingOrder.customer || {}).name || `${(terminatingOrder.customer || {}).firstName || ''} ${(terminatingOrder.customer || {}).lastName || ''}`.trim()}</p>
                  </div>
                </div>
                <button
                  onClick={() => { if (!terminateLoading) { setTerminatingOrder(null); setTerminateReason(''); } }}
                  className="text-slate-400 hover:text-white"
                  disabled={terminateLoading}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-sm text-slate-300 space-y-1.5">
                  <p>Vous résiliez cette location <strong className="text-white">à la place du client</strong> (il n'a pas résilié sur son espace). En confirmant :</p>
                  <ul className="list-disc list-inside text-slate-400 space-y-1">
                    <li>L'abonnement Stripe est <strong className="text-white">stoppé</strong> — plus aucun prélèvement.</li>
                    <li>La commande <strong className="text-white">bascule dans l'onglet « Résiliation »</strong>.</li>
                    <li>Vous poursuivez ensuite les étapes habituelles (bordereau / réception / remise en stock).</li>
                  </ul>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Motif (optionnel)</label>
                  <textarea
                    value={terminateReason}
                    onChange={(e) => setTerminateReason(e.target.value)}
                    rows={2}
                    maxLength={500}
                    placeholder="Ex: Matériel rapporté au dépôt, client n'a pas résilié en ligne"
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#DFFF00] resize-none"
                    data-testid="terminate-reason-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-red-400 uppercase tracking-wide mb-1.5">Mot de passe de confirmation *</label>
                  <input
                    type="password"
                    value={terminatePassword}
                    onChange={(e) => setTerminatePassword(e.target.value)}
                    placeholder="Saisissez le mot de passe d'action"
                    autoComplete="off"
                    className="w-full bg-slate-800 border border-red-500/40 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-red-500"
                    data-testid="terminate-password-input"
                    onKeyDown={(e) => { if (e.key === 'Enter' && terminatePassword && !terminateLoading) submitTerminate(); }}
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Double sécurité : cette action arrête l'abonnement et est irréversible côté facturation.</p>
                </div>
              </div>
              <div className="flex gap-3 p-4 border-t border-slate-800">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  onClick={() => { setTerminatingOrder(null); setTerminateReason(''); setTerminatePassword(''); }}
                  disabled={terminateLoading}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  onClick={submitTerminate}
                  disabled={terminateLoading || !terminatePassword}
                  data-testid="confirm-terminate-rental-btn"
                >
                  {terminateLoading ? 'Résiliation…' : 'Confirmer la résiliation'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {deletingOrder && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="delete-order-modal">
            <div className="bg-slate-900 rounded-xl w-full max-w-md overflow-hidden border border-red-500/30">
              <div className="flex items-center justify-between p-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">{deleteMode === 'permanent' ? 'Supprimer définitivement' : 'Déplacer dans la corbeille'}</h2>
                    <p className="text-xs text-slate-400">Commande #{deletingOrder.order_number || deletingOrder.id?.slice(0, 8)}</p>
                  </div>
                </div>
                <button
                  onClick={() => { if (!deleteLoading) { setDeletingOrder(null); setDeletePassword(''); setDeleteMode('soft'); } }}
                  className="text-slate-400 hover:text-white"
                  disabled={deleteLoading}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                {deleteMode === 'permanent' ? (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">
                    <p className="font-semibold mb-1">⚠️ Action irréversible</p>
                    <p className="text-xs">
                      La commande sera <strong>définitivement effacée</strong> de la base. Cette action ne peut pas être annulée.
                    </p>
                  </div>
                ) : (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-300">
                    <p className="font-semibold mb-1">↩️ Action réversible</p>
                    <p className="text-xs">
                      La commande sera <strong>déplacée dans la corbeille</strong> (masquée de la liste mais conservée).
                      Vous pourrez la <strong>restaurer</strong> à tout moment. Aucun remboursement Stripe ne sera déclenché.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-xs text-slate-400">Client</p>
                  <p className="text-white">{deletingOrder.customer?.firstName} {deletingOrder.customer?.lastName} — {deletingOrder.customer?.email}</p>
                  <p className="text-xs text-slate-400 mt-2">Pack</p>
                  <p className="text-white">{deletingOrder.pack_name}</p>
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-slate-400">Mot de passe administrateur <span className="text-red-400">*</span></label>
                  <Input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Mot de passe requis"
                    className="bg-slate-800 border-slate-700 text-white"
                    disabled={deleteLoading}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter' && deletePassword) handleConfirmDeleteOrder(); }}
                    data-testid="delete-order-password-input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-800">
                <Button
                  variant="ghost"
                  onClick={() => { setDeletingOrder(null); setDeletePassword(''); setDeleteMode('soft'); }}
                  disabled={deleteLoading}
                  className="text-slate-400 hover:text-white"
                  data-testid="delete-order-cancel-btn"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleConfirmDeleteOrder}
                  disabled={deleteLoading || !deletePassword}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="delete-order-confirm-btn"
                >
                  {deleteLoading
                    ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Traitement...</>)
                    : deleteMode === 'permanent'
                      ? (<><Trash2 className="w-4 h-4 mr-2" />Supprimer définitivement</>)
                      : (<><Trash2 className="w-4 h-4 mr-2" />Déplacer dans la corbeille</>)}
                </Button>
              </div>
            </div>
          </div>
        )}


        {/* Client Dossier Modal */}
        {clientDossier && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Dossier client</h2>
                    <p className="text-sm text-slate-400">Commande #{clientDossier.id?.slice(0, 8)}</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-slate-400 hover:text-white"
                  onClick={() => setClientDossier(null)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="p-4 overflow-y-auto flex-1 space-y-4">
                {loadingDossier ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-[#DFFF00] animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Client Info */}
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <User className="w-5 h-5 text-[#DFFF00]" />
                        <span className="font-medium text-white">Informations client</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Nom complet</p>
                          <p className="text-white font-medium">{clientDossier.customer?.firstName} {clientDossier.customer?.lastName}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Email</p>
                          <p className="text-white">{clientDossier.customer?.email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Téléphone</p>
                          <p className="text-white">{clientDossier.customer?.phone || 'Non renseigné'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Entreprise</p>
                          <p className="text-white">{clientDossier.customer?.company || 'Particulier'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">SIREN</p>
                          <p className="text-white font-mono">{clientDossier.customer?.siren || 'Non renseigné'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Type d'activité</p>
                          <span className={`inline-block px-2 py-0.5 text-xs rounded-full border ${getActivityTypeBadgeClass(clientDossier.customer?.activityType)}`}>
                            {formatActivityType(clientDossier.customer?.activityType)}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs text-slate-500 mb-1">Adresse de livraison</p>
                          <p className="text-white">{clientDossier.customer?.address}</p>
                          <p className="text-white">{clientDossier.customer?.postalCode} {clientDossier.customer?.city}</p>
                        </div>
                      </div>
                    </div>

                    {/* Pack Info */}
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Package className="w-5 h-5 text-[#DFFF00]" />
                        <span className="font-medium text-white">Pack demandé</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Pack</p>
                          <p className="text-white font-medium"><PackName name={clientDossier.pack_name} /></p>
                          <p className="text-sm text-slate-400">{clientDossier.pack_surface}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Tarif mensuel</p>
                          <p className="text-[#DFFF00] font-bold">{formatPrice(clientDossier.monthly_price)}/mois</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Caution payée</p>
                          <p className="text-white font-medium">{formatPrice(clientDossier.caution)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Date de demande</p>
                          <p className="text-white">{formatDate(clientDossier.created_at)}</p>
                        </div>
                        {clientDossier.rental_start_date && (
                          <div className="col-span-2">
                            <p className="text-xs text-slate-500 mb-1">Date de début souhaitée</p>
                            <p className="text-[#DFFF00]">{formatDate(clientDossier.rental_start_date)}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* RC Document */}
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Shield className="w-5 h-5 text-[#DFFF00]" />
                        <span className="font-medium text-white">Attestation RC Professionnelle</span>
                      </div>
                      {clientDossier.rcDocument?.rc_document_url ? (
                        <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-6 h-6 text-green-400" />
                            <div>
                              <p className="text-green-400 font-medium">Document fourni</p>
                              <p className="text-sm text-slate-400">{clientDossier.rcDocument.rc_document_name || 'attestation_rc.pdf'}</p>
                            </div>
                          </div>
                          <a 
                            href={`${API.replace('/api', '')}${clientDossier.rcDocument.rc_document_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 px-4 py-2 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            Voir le document
                          </a>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                          <AlertTriangle className="w-6 h-6 text-red-400" />
                          <div>
                            <p className="text-red-400 font-medium">Document manquant</p>
                            <p className="text-sm text-slate-400">Aucune attestation RC Pro n'a été fournie</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Contract Signature */}
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <FileText className="w-5 h-5 text-[#DFFF00]" />
                        <span className="font-medium text-white">Contrat</span>
                      </div>
                      {clientDossier.contract_signature ? (
                        <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-6 h-6 text-green-400" />
                            <div>
                              <p className="text-green-400 font-medium">Contrat signé électroniquement</p>
                              <p className="text-sm text-slate-400">Signé le {formatDate(clientDossier.contract_accepted_at || clientDossier.created_at)}</p>
                            </div>
                          </div>
                          <Button
                            onClick={() => window.open(`${API}/contracts/${clientDossier.id}/pdf`, '_blank')}
                            className="flex items-center gap-2 bg-[#DFFF00]/20 hover:bg-[#DFFF00]/30 text-[#DFFF00] border border-[#DFFF00]/30"
                          >
                            <Eye className="w-4 h-4" />
                            Voir le contrat
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <AlertTriangle className="w-6 h-6 text-yellow-400" />
                            <div>
                              <p className="text-yellow-400 font-medium">En attente de signature</p>
                              <p className="text-sm text-slate-400">Le contrat sera signé via PandaDoc</p>
                            </div>
                          </div>
                          <Button
                            onClick={() => window.open(`${API}/contracts/${clientDossier.id}/pdf`, '_blank')}
                            variant="outline"
                            className="flex items-center gap-2 border-slate-600 text-slate-300 hover:bg-slate-700"
                          >
                            <Eye className="w-4 h-4" />
                            Aperçu contrat
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Protect + Avenant (only if subscribed) */}
                    {clientDossier.protect_plus && (
                      <div className="bg-slate-800/50 rounded-lg p-4" data-testid="admin-protect-avenant-block">
                        <div className="flex items-center gap-2 mb-4">
                          <Shield className="w-5 h-5 text-[#DFFF00]" />
                          <span className="font-medium text-white">Couverture Protect +</span>
                        </div>
                        <div className="flex items-center justify-between bg-[#DFFF00]/10 border border-[#DFFF00]/30 rounded-lg p-4">
                          <div>
                            <p className="text-[#DFFF00] font-medium">Protect + souscrit</p>
                            <p className="text-sm text-slate-400">
                              Forfait {clientDossier.protect_plus_fee}€/mois · Caution réduite {clientDossier.caution}€
                              {clientDossier.caution_standard ? ` (au lieu de ${clientDossier.caution_standard}€)` : ''}
                            </p>
                          </div>
                          <Button
                            onClick={() => window.open(`${API}/contracts/${clientDossier.id}/avenant-pdf`, '_blank')}
                            className="flex items-center gap-2 bg-[#DFFF00]/20 hover:bg-[#DFFF00]/30 text-[#DFFF00] border border-[#DFFF00]/30"
                            data-testid="admin-view-avenant-btn"
                          >
                            <Eye className="w-4 h-4" />
                            Voir l'avenant
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Payment Status */}
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <CreditCard className="w-5 h-5 text-[#DFFF00]" />
                        <span className="font-medium text-white">Paiement</span>
                      </div>
                      <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                        <CheckCircle className="w-6 h-6 text-green-400" />
                        <div>
                          <p className="text-green-400 font-medium">Paiement reçu</p>
                          <p className="text-sm text-slate-400">
                            Premier paiement de {formatPrice((clientDossier.monthly_price || 0) + (clientDossier.caution || 0))} (location + caution)
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Modal Footer with Actions */}
              <div className="p-4 border-t border-slate-800 flex justify-between">
                <Button 
                  variant="outline" 
                  className="border-slate-700 text-slate-300"
                  onClick={() => setClientDossier(null)}
                >
                  Fermer
                </Button>
                <div className="flex gap-3">
                  <Button 
                    className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                    onClick={() => { handleRejectBooking(clientDossier.id); setClientDossier(null); }}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Refuser
                  </Button>
                  <Button 
                    className="bg-[#DFFF00] hover:bg-[#c8e600] text-black"
                    onClick={() => { handleApproveBooking(clientDossier.id); setClientDossier(null); }}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Approuver la commande
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status / Payment Editor Modal */}
        {statusEditOrder && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="status-edit-modal">
            <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-md p-6">
              <h3 className="text-lg font-bold text-white mb-1">Modifier le statut</h3>
              <p className="text-sm text-slate-400 mb-5 truncate">
                {statusEditOrder.customer_name || statusEditOrder.customer?.firstName || 'Commande'} · {statusEditOrder.pack_name || ''}
              </p>

              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Statut de la commande</label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                className="w-full mb-4 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#DFFF00]"
                data-testid="status-edit-order-select"
              >
                <option value="pending_payment">En attente de paiement</option>
                <option value="pending_validation">À valider</option>
                <option value="confirmed">Confirmé</option>
                <option value="shipped">Expédié</option>
                <option value="delivered">Livré</option>
                <option value="active">Actif</option>
                <option value="return_pending">Retour en attente</option>
                <option value="returned">Retourné</option>
                <option value="cancelled">Annulé</option>
                <option value="rejected">Refusé</option>
                <option value="terminated">Terminé</option>
              </select>

              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Statut du paiement</label>
              <select
                value={editPayment}
                onChange={(e) => setEditPayment(e.target.value)}
                className="w-full mb-6 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#DFFF00]"
                data-testid="status-edit-payment-select"
              >
                <option value="pending">En attente</option>
                <option value="paid">Payé</option>
                <option value="refunded">Remboursé</option>
                <option value="failed">Échoué</option>
              </select>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  onClick={() => setStatusEditOrder(null)}
                  disabled={statusSaving}
                  data-testid="status-edit-cancel"
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold"
                  onClick={saveStatus}
                  disabled={statusSaving}
                  data-testid="status-edit-save"
                >
                  {statusSaving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  

}

export default OrdersView;
